import { Head, useForm } from "@inertiajs/react"
import { Camera } from "lucide-react"
import { useRef, useState } from "react"

import CountryCombobox from "@/components/country-combobox"
import DatePicker from "@/components/date-picker"
import DivisionCombobox from "@/components/division-combobox"
import FileUpload from "@/components/file-upload"
import LocationSelect from "@/components/location-select"
import PositionPicker from "@/components/position-picker"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import { POSITIONS } from "@/lib/positions"
import type PerfilEdit from "@/types/serializers/PerfilEdit"
import type RequestsUpdatePlayerProfile from "@/types/serializers/RequestsUpdatePlayerProfile"

const FOOT_OPTIONS = [
  { value: "derecho", label: "Derecho" },
  { value: "izquierdo", label: "Izquierdo" },
  { value: "ambidiestro", label: "Ambidiestro" },
]

const AVAILABILITY_OPTIONS = [
  { value: "disponible", label: "Disponible" },
  { value: "no_disponible", label: "No disponible" },
  { value: "escuchando_ofertas", label: "Escuchando ofertas" },
]

const VISIBILITY_OPTIONS = [
  { value: "publico", label: "Público" },
  { value: "solo_scouts", label: "Solo Scouts" },
  { value: "privado", label: "Privado" },
]

export default function PlayerEdit({ player }: PerfilEdit) {
  const [avatarUploaded, setAvatarUploaded] = useState(false)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(
    player.avatar_url,
  )
  const prevCountryRef = useRef(player.country)

  const { data, setData, patch, processing, errors } =
    useForm<RequestsUpdatePlayerProfile>({
      player_profile: {
        position: player.position,
        secondary_position: player.secondary_position ?? "",
        dominant_foot: player.dominant_foot,
        date_of_birth: player.date_of_birth,
        nationality: player.nationality ?? "",
        country: player.country,
        region: player.region ?? "",
        city: player.city ?? "",
        height_cm: player.height_cm ?? null,
        weight_kg: player.weight_kg ?? null,
        current_club: player.current_club ?? "",
        current_division: player.current_division ?? "",
        availability: player.availability,
        visibility: player.visibility,
        bio: player.bio ?? "",
        idol: player.idol ?? "",
        avatar: null,
      },
    })

  const setField = (
    field: keyof typeof data.player_profile,
    value: string | number | null,
  ) =>
    setData("player_profile", {
      ...data.player_profile,
      [field]: value,
    } as typeof data.player_profile)

  function handleCountryChange(newCountry: string) {
    const shouldAutoFill =
      data.player_profile.nationality === "" ||
      data.player_profile.nationality === prevCountryRef.current
    setData("player_profile", {
      ...data.player_profile,
      country: newCountry,
      region: "",
      city: "",
      ...(shouldAutoFill ? { nationality: newCountry } : {}),
    })
    prevCountryRef.current = newCountry
  }

  function handleRegionChange(newRegion: string) {
    setData("player_profile", {
      ...data.player_profile,
      region: newRegion,
      city: "",
    })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch("/perfil")
  }

  return (
    <AppLayout>
      <Head title="Editar perfil | KrownFT" />

      <div className="mx-auto max-w-2xl px-6 py-8 pb-24">
        <h1 className="mb-6 text-2xl font-bold">Editar perfil</h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Foto de perfil */}
          <section>
            <h2 className="mb-4 text-lg font-semibold">Foto de perfil</h2>
            <div className="flex items-center gap-6">
              <div className="group relative">
                <Avatar className="h-24 w-24">
                  {avatarPreview ? (
                    <AvatarImage
                      src={avatarPreview}
                      alt="Foto de perfil actual"
                      className="object-cover"
                    />
                  ) : null}
                  <AvatarFallback className="bg-krown-600 text-2xl font-bold text-white">
                    {player.user.first_name[0]}
                    {player.user.last_name[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                  <Camera className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <FileUpload
                  accept="image"
                  onUploadComplete={(signedId, file) => {
                    setField("avatar", signedId)
                    setAvatarUploaded(true)
                    const reader = new FileReader()
                    reader.onload = (e) =>
                      setAvatarPreview(e.target?.result as string)
                    reader.readAsDataURL(file)
                  }}
                />
                {avatarUploaded && (
                  <p className="mt-2 text-xs text-green-400">
                    Foto subida correctamente
                  </p>
                )}
                {errors["player_profile.avatar"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["player_profile.avatar"]}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Datos Personales */}
          <section>
            <h2 className="mb-4 text-lg font-semibold">Datos Personales</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <DatePicker
                label="Fecha de nacimiento"
                value={String(data.player_profile.date_of_birth)}
                onChange={(v) => setField("date_of_birth", v)}
                error={errors["player_profile.date_of_birth"]?.[0]}
              />
              <CountryCombobox
                label="Nacionalidad"
                value={String(data.player_profile.nationality)}
                onChange={(v) => setField("nationality", v)}
                placeholder="Seleccionar nacionalidad..."
                error={errors["player_profile.nationality"]?.[0]}
              />
            </div>
            <div className="mt-4 grid gap-4">
              <LocationSelect
                country={data.player_profile.country}
                region={String(data.player_profile.region)}
                city={String(data.player_profile.city)}
                onCountryChange={handleCountryChange}
                onRegionChange={handleRegionChange}
                onCityChange={(v) => setField("city", v)}
                errors={{
                  country: errors["player_profile.country"]?.[0],
                  region: errors["player_profile.region"]?.[0],
                  city: errors["player_profile.city"]?.[0],
                }}
              />
            </div>
          </section>

          {/* Info Deportiva */}
          <section>
            <h2 className="mb-4 text-lg font-semibold">Info Deportiva</h2>
            <div className="grid gap-4">
              {/* Position picker - visual field on normal screens, dropdown fallback on very small screens */}
              <div className="space-y-1.5">
                <Label>Posición</Label>
                <div className="hidden min-[320px]:block">
                  <PositionPicker
                    value={data.player_profile.position || null}
                    onChange={(val) => setField("position", val ?? "")}
                    secondaryValue={
                      data.player_profile.secondary_position || null
                    }
                    onSecondaryChange={(val) =>
                      setField("secondary_position", val ?? "")
                    }
                  />
                  <p className="text-muted-foreground mt-1 text-center text-xs">
                    Tocá para elegir posición principal. Tocá otra para
                    secundaria.
                  </p>
                </div>
                {/* Dropdown fallback for very small screens (<320px) */}
                <div className="space-y-4 min-[320px]:hidden">
                  <Select
                    value={data.player_profile.position}
                    onValueChange={(v) => setField("position", v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Posición principal" />
                    </SelectTrigger>
                    <SelectContent>
                      {POSITIONS.map((pos) => (
                        <SelectItem key={pos.value} value={pos.value}>
                          {pos.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={String(data.player_profile.secondary_position)}
                    onValueChange={(v) => setField("secondary_position", v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Posición secundaria (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {POSITIONS.map((pos) => (
                        <SelectItem key={pos.value} value={pos.value}>
                          {pos.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {errors["player_profile.position"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["player_profile.position"]}
                  </p>
                )}
                {errors["player_profile.secondary_position"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["player_profile.secondary_position"]}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <FormField
                label="Pie dominante"
                error={errors["player_profile.dominant_foot"]?.[0]}
              >
                <Select
                  value={data.player_profile.dominant_foot}
                  onValueChange={(v) => setField("dominant_foot", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {FOOT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField
                label="Altura (cm)"
                error={errors["player_profile.height_cm"]?.[0]}
              >
                <Input
                  type="number"
                  value={String(data.player_profile.height_cm)}
                  onChange={(e) => setField("height_cm", e.target.value)}
                  placeholder="Ej: 175"
                />
              </FormField>
              <FormField
                label="Peso (kg)"
                error={errors["player_profile.weight_kg"]?.[0]}
              >
                <Input
                  type="number"
                  value={String(data.player_profile.weight_kg)}
                  onChange={(e) => setField("weight_kg", e.target.value)}
                  placeholder="Ej: 72"
                />
              </FormField>
            </div>
          </section>

          {/* Situación Actual */}
          <section>
            <h2 className="mb-4 text-lg font-semibold">Situación Actual</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label="Club actual"
                error={errors["player_profile.current_club"]?.[0]}
              >
                <Input
                  value={String(data.player_profile.current_club)}
                  onChange={(e) => setField("current_club", e.target.value)}
                  placeholder="Ej: Rosario Central"
                />
              </FormField>
              <DivisionCombobox
                country={data.player_profile.country}
                value={String(data.player_profile.current_division)}
                onChange={(v) => setField("current_division", v)}
                error={errors["player_profile.current_division"]?.[0]}
              />
              <FormField
                label="Disponibilidad"
                error={errors["player_profile.availability"]?.[0]}
              >
                <Select
                  value={data.player_profile.availability}
                  onValueChange={(v) => setField("availability", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABILITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField
                label="Visibilidad"
                error={errors["player_profile.visibility"]?.[0]}
              >
                <Select
                  value={data.player_profile.visibility}
                  onValueChange={(v) => setField("visibility", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {VISIBILITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </div>
          </section>

          {/* Sobre Mí */}
          <section>
            <h2 className="mb-4 text-lg font-semibold">Sobre Mí</h2>
            <div className="space-y-4">
              <FormField label="Bio" error={errors["player_profile.bio"]?.[0]}>
                <Textarea
                  value={String(data.player_profile.bio)}
                  onChange={(e) => setField("bio", e.target.value)}
                  placeholder="Contá algo sobre vos..."
                  maxLength={500}
                  rows={4}
                />
                <p className="mt-1 text-xs text-neutral-500">
                  {String(data.player_profile.bio).length}/500
                </p>
              </FormField>
              <FormField
                label="Ídolo"
                error={errors["player_profile.idol"]?.[0]}
              >
                <Input
                  value={String(data.player_profile.idol)}
                  onChange={(e) => setField("idol", e.target.value)}
                  placeholder="Ej: Lionel Messi"
                />
              </FormField>
            </div>
          </section>

          <Button type="submit" disabled={processing} className="w-full">
            Guardar cambios
          </Button>
        </form>
      </div>
    </AppLayout>
  )
}

function FormField({
  label,
  error,
  children,
}: {
  label: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  )
}
